import { useI18n } from "@/composables/useI18n";
import type { Transaction } from "@/utils/types";
import { getTrackedCategoryAllocations } from "@aircap/common/trackedCategory";
import { computed, ref, type Ref } from "vue";

export interface TrackedCategoryForChart {
  id: string;
  name: string;
}

function nextYearMonth(ym: string): string {
  const [y, m] = ym.split("-");
  if (!y || !m) throw new Error("Invalid year-month format");
  const month = parseInt(m, 10);
  const year = parseInt(y, 10) + (month === 12 ? 1 : 0);
  const nextM = month === 12 ? 1 : month + 1;
  return `${year}-${nextM.toString().padStart(2, "0")}`;
}

export const useCharts = (
  transactions: Ref<Transaction[] | null>,
  trackedCategories?: Ref<TrackedCategoryForChart[]>,
) => {
  const { t, locale } = useI18n();
  const filteredDates = ref<string[]>([]);

  const hasTrackedCategories = computed(() => (trackedCategories?.value?.length ?? 0) > 0);

  const dateSubtitle = computed(() => {
    if (!transactions.value) return t("common.loading");

    if (transactions.value.length === 0) return t("transactions.noTransactions");

    const sortedDates = transactions.value
      .map((tx) => tx.date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const earliestDate = sortedDates[0];
    const latestDate = sortedDates[sortedDates.length - 1];
    if (!earliestDate || !latestDate) return t("transactions.noTransactions");

    const firstDateValue = filteredDates.value[0] || earliestDate;
    const lastDateValue = filteredDates.value[filteredDates.value.length - 1] || latestDate;

    const dateFormatter = new Intl.DateTimeFormat(locale.value);

    if (filteredDates.value.length === 1) {
      return dateFormatter.format(new Date(firstDateValue));
    }

    const startDate = dateFormatter.format(new Date(firstDateValue));
    const endDate = dateFormatter.format(new Date(lastDateValue));
    return t("charts.dateRange.fromTo", { startDate, endDate });
  });

  const categoriesChartData = computed(() => {
    const dates = new Set(
      filteredDates.value.map((str) => {
        const date = new Date(str);

        return Intl.DateTimeFormat("fr-CA").format(date);
      }),
    );

    const byCat = new Map<string, number>();
    const trackedCategoryNameById = new Map(
      (trackedCategories?.value ?? []).map((c) => [c.id, c.name]),
    );

    if (hasTrackedCategories.value && trackedCategories?.value) {
      for (const tx of transactions.value ?? []) {
        const inRange = dates.size > 0 ? dates.has(tx.date) : true;
        if (!inRange) continue;
        if (tx.amount > 0) continue;

        const allocations = getTrackedCategoryAllocations(tx);
        if (allocations.length === 0) {
          byCat.set("—", (byCat.get("—") ?? 0) + Math.abs(tx.amount));
          continue;
        }

        let allocatedTotal = 0;
        for (const allocation of allocations) {
          const key = trackedCategoryNameById.get(allocation.trackedCategoryId) ?? "—";
          byCat.set(key, (byCat.get(key) ?? 0) + allocation.amount);
          allocatedTotal += allocation.amount;
        }

        const remainder = Math.abs(tx.amount) - allocatedTotal;
        if (remainder > 0.01) {
          byCat.set("—", (byCat.get("—") ?? 0) + remainder);
        }
      }
    } else {
      for (const tx of transactions.value ?? []) {
        const inRange = dates.size > 0 ? dates.has(tx.date) : true;
        if (!inRange) continue;
        if (!tx.category?.name) continue;
        if (tx.amount > 0) continue;
        const key = tx.category.name;
        byCat.set(key, (byCat.get(key) ?? 0) + Math.abs(tx.amount));
      }
    }

    return Array.from(byCat, ([name, value]) => ({ name, value }));
  });

  const barChartData = computed(() => {
    if ((transactions.value ?? []).length < 1) return [];

    const oldest = (transactions.value ?? []).reduce<Transaction | null>((acc, curr) => {
      if (!acc) return curr;
      if (new Date(acc.date) < new Date(curr.date)) return acc;
      return curr;
    }, null);

    if (!oldest) return [];

    const byMonth = new Map<string, { positive: number; negative: number; date: string }>();

    for (const t of transactions.value ?? []) {
      const month = t.date.split("-").slice(0, 2).join("-");
      let monthEntry = byMonth.get(month);
      if (!monthEntry) {
        monthEntry = { positive: 0, negative: 0, date: month };
        byMonth.set(month, monthEntry);
      }
      if (t.amount > 0) {
        monthEntry.positive += t.amount;
      } else {
        monthEntry.negative += t.amount;
      }
    }

    const values: {
      positive: number;
      negative: number;
      date: string;
    }[] = [];

    let currentDate = oldest.date.split("-").slice(0, 2).join("-");

    const now = new Date();
    const stopDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const stopFormatted = `${stopDate.getFullYear()}-${(stopDate.getMonth() + 1).toString().padStart(2, "0")}`;

    while (currentDate < stopFormatted) {
      values.push(byMonth.get(currentDate) ?? { positive: 0, negative: 0, date: currentDate });
      currentDate = nextYearMonth(currentDate);
    }

    return values;
  });

  return {
    dateSubtitle,
    filteredDates,
    categoriesChartData,
    barChartData,
  };
};
